import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useClubMember } from "@/hooks/useClubMember";
import { SubscribeModal } from "@/components/research/SubscribeModal";
import { FoundingMemberModal } from "@/components/founding/FoundingMemberModal";

// The Club is kept for its existing members and closed to new sign-ups (his ruling, 30 Oct 2026). Every
// Club checkout opens through these two wrappers: a Club member (or a signed-out reader, who must sign in
// first anyway) gets the checkout exactly as before; an account made after the cutoff gets a short note.
// The checkout modals themselves are not touched.
// Every join, subscribe and Founding button sits inside ClubJoinGate (his follow-up, 30 Oct 2026): a signed-out
// visitor and a signed-in non-member see the notice below in its place; members see the buttons as before.
type Props = { isOpen: boolean; onClose: () => void };

// One wording everywhere the Club is offered (his words, 30 Oct 2026).
export const CLUB_OPEN_TO = "The Club is open to existing members.";
export const CLUB_CLOSED_UNTIL = "New memberships are closed until further notice.";
const NEW_READERS = "New readers can follow the work in the Alpha with AI newsletter, and use SN Data for the numbers.";

/** True only for a signed-in Club member; waits while the membership check runs. */
export const useClubJoinOpen = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { isClubMember, isLoading } = useClubMember(user);
  return { open: !!user && isClubMember, user, waiting: authLoading || (!!user && isLoading) };
};

/** The notice that stands in for the Club's join buttons. Signed out: "Members, sign in", back to this page. */
export const ClubClosedNotice = ({ compact = false, className = "" }: { compact?: boolean; className?: string }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const signIn = `/login?redirect=${encodeURIComponent(pathname)}`;
  if (compact) {
    return (
      <p className={`text-[13px] leading-[1.5] text-muted-foreground ${className}`}>
        {CLUB_OPEN_TO} {CLUB_CLOSED_UNTIL}
        {!user && <> <Link to={signIn} className="font-medium text-primary hover:underline">Members, sign in</Link></>}
      </p>
    );
  }
  return (
    <div className={`rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))] p-4 ${className}`}>
      <p className="text-[14.5px] font-semibold leading-[1.45] text-foreground">{CLUB_OPEN_TO} {CLUB_CLOSED_UNTIL}</p>
      <p className="mt-1.5 text-[13.5px] leading-[1.55] text-muted-foreground">{NEW_READERS}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        {!user && (
          <Link to={signIn} className="inline-flex h-10 items-center justify-center rounded-[3px] bg-primary px-[18px] text-[14px] font-semibold text-primary-foreground transition hover:brightness-110">
            Members, sign in
          </Link>
        )}
        <a href="https://ai.shikshannivesh.com/" target="_blank" rel="noopener noreferrer" className="inline-flex h-10 items-center rounded-[3px] border border-[var(--sn-border-strong)] px-4 text-[14px] font-medium text-foreground hover:border-foreground">
          Read the newsletter
        </a>
        <Link to="/data" className="inline-flex h-10 items-center rounded-[3px] border border-[var(--sn-border-strong)] px-4 text-[14px] font-medium text-foreground hover:border-foreground">
          Open SN Data
        </Link>
      </div>
    </div>
  );
};

/** Shows its buttons to Club members only; everyone else gets the notice, or nothing with `quiet` (small
 *  buttons repeated down a list, where one notice on the page is enough). */
export const ClubJoinGate = ({ children, compact, quiet, className }: { children: ReactNode; compact?: boolean; quiet?: boolean; className?: string }) => {
  const { open, waiting } = useClubJoinOpen();
  if (waiting) return null;
  if (open) return <>{children}</>;
  return quiet ? null : <ClubClosedNotice compact={compact} className={className} />;
};

export const ClubClosedNote = ({ isOpen, onClose }: Props) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="club-closed-title"
        className="relative w-full max-w-md rounded-xl border border-[var(--sn-border)] bg-background p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 rounded p-1 text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
        <h2 id="club-closed-title" className="mb-2 text-lg font-semibold text-foreground">{CLUB_OPEN_TO}</h2>
        <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
          {CLUB_CLOSED_UNTIL} {NEW_READERS}
        </p>
        <div className="flex flex-wrap gap-3">
          <a href="https://ai.shikshannivesh.com/" target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground hover:brightness-110">
            Read the newsletter
          </a>
          <Link to="/data" onClick={onClose} className="inline-flex h-9 items-center rounded-lg border border-[var(--sn-border-strong)] px-4 text-sm font-medium text-foreground hover:border-primary">
            Open SN Data
          </Link>
        </div>
      </div>
    </div>
  );
};

const useClosedToThisAccount = () => {
  const { user } = useAuth();
  const { isClubMember, isLoading } = useClubMember(user);
  return { closed: !!user && !isLoading && !isClubMember, waiting: !!user && isLoading };
};

export const ClubSubscribeModal = ({ isOpen, onClose }: Props) => {
  const { closed, waiting } = useClosedToThisAccount();
  if (waiting) return null;
  return closed ? <ClubClosedNote isOpen={isOpen} onClose={onClose} /> : <SubscribeModal isOpen={isOpen} onClose={onClose} />;
};

export const ClubFoundingModal = ({ isOpen, onClose }: Props) => {
  const { closed, waiting } = useClosedToThisAccount();
  if (waiting) return null;
  return closed ? <ClubClosedNote isOpen={isOpen} onClose={onClose} /> : <FoundingMemberModal isOpen={isOpen} onClose={onClose} />;
};
